import { useEffect, useRef, useState } from 'react'
import commentService from '../services/commentService'

/**
 * 评论数据管理 Hook
 * - 加载评论与分页
 * - 新增顶层评论与回复
 * - 展开/收起回复
 * - 点赞状态同步
 */
export default function useComments(contentId, initialPage = 1, perPage = 20) {
  const [comments, setComments] = useState([])
  const [pagination, setPagination] = useState({ page: initialPage, per_page: perPage, total: 0, total_pages: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const expandedMapRef = useRef(new Map())

  /**
   * 拉取评论列表（支持定位特定评论）
   */
  const loadComments = async (page = pagination.page, per_page = pagination.per_page, targetCommentId) => {
    setLoading(true)
    try {
      const res = await commentService.getComments(contentId, page, per_page, targetCommentId)
      const list = Array.isArray(res?.comments) ? res.comments : []
      const transformed = list.map((c) => ({
        ...c,
        id: c.id,
        content: c.fields?.content || '',
        user: {
          id: c.user?.id || c.author_id,
          name: c.user?.name || c.user?.username || 'Unknown User',
          avatar: c.user?.avatar_url || c.user?.avatar || null,
          role: c.user?.role,
        },
        replies: c.replies ? { ...c.replies, list: (c.replies.list || []).map((r) => ({
          ...r,
          user: { ...r.user, name: r.user?.name || r.user?.username || 'Unknown User' },
        })) } : { list: [], total_count: 0, has_more: false },
      }))
      setComments(page === 1 ? transformed : [...comments, ...transformed])
      const meta = res?.metadata || {}
      setPagination({ page, per_page, total: meta.total || transformed.length, total_pages: meta.total_pages || 1 })
      setError(null)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!contentId) return
    loadComments(initialPage, perPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId])

  /**
   * 在列表顶部插入新评论
   */
  const addCommentTop = (newComment) => {
    const transformed = {
      ...newComment,
      id: newComment.id,
      content: newComment.fields?.content || '',
      user: {
        id: newComment.user?.id || newComment.author_id,
        name: newComment.user?.name || newComment.user?.username || 'Unknown User',
        avatar: newComment.user?.avatar_url || newComment.user?.avatar || null,
        role: newComment.user?.role,
      },
      replies: { list: [], total_count: 0, has_more: false },
    }
    setComments((prev) => [transformed, ...prev])
    setPagination((prev) => ({ ...prev, total: (prev.total || 0) + 1 }))
  }

  /**
   * 在父评论下插入新回复，并保持唯一性
   */
  const addReplyTop = (parentCommentId, newReply, replyToUserId, replyToUserName) => {
    setComments((prev) => prev.map((c) => {
      if (String(c.id) !== String(parentCommentId)) return c
      const transformedReply = {
        ...newReply,
        fields: { ...newReply.fields, ...(replyToUserId ? { reply_to_user_id: replyToUserId } : {}) },
        user: {
          id: newReply.user?.id || newReply.author_id,
          name: newReply.user?.name || newReply.user?.username || 'Unknown User',
          avatar: newReply.user?.avatar_url || newReply.user?.avatar || null,
          role: newReply.user?.role,
        },
        ...(replyToUserId && replyToUserName ? { reply_to_user: { id: replyToUserId, name: replyToUserName } } : {}),
      }
      const list = Array.isArray(c.replies?.list) ? c.replies.list : []
      const ids = new Set(list.map((r) => String(r.id)))
      const newList = ids.has(String(transformedReply.id)) ? list : [transformedReply, ...list]
      expandedMapRef.current.set(parentCommentId, true)
      return { ...c, replies: { ...c.replies, list: newList, total_count: (c.replies?.total_count || 0) + (ids.has(transformedReply.id) ? 0 : 1) } }
    }))
  }

  /**
   * 展开并拉取更多回复
   */
  const expandReplies = async (commentId, page = 1, per_page = 100) => {
    const res = await commentService.getCommentReplies(commentId, page, per_page)
    const incoming = Array.isArray(res?.replies) ? res.replies : []
    setComments((prev) => prev.map((c) => {
      if (String(c.id) !== String(commentId)) return c
      const existingIds = new Set((c.replies?.list || []).map((r) => String(r.id)))
      const newReplies = page === 1 ? incoming.filter((r) => !existingIds.has(String(r.id))) : incoming
      const updated = [...(c.replies?.list || []), ...newReplies]
      expandedMapRef.current.set(commentId, true)
      return { ...c, replies: { ...c.replies, list: updated, total_count: c.replies?.total_count || updated.length, has_more: (res?.metadata?.page || 1) < (res?.metadata?.total_pages || 1) } }
    }))
  }

  /**
   * 判断某评论回复是否处于展开状态
   */
  const isExpanded = (commentId) => {
    return expandedMapRef.current.get(commentId) === true
  }

  /**
   * 切换点赞状态（支持评论和回复）
   */
  const toggleLike = async (contentIdOrCommentId) => {
    const res = await commentService.toggleLike(contentIdOrCommentId)
    setComments((prev) => prev.map((c) => {
      if (String(c.id) === String(contentIdOrCommentId)) {
        return { ...c, is_liked: res?.liked || false, interactions: { ...c.interactions, like_count: res?.like_count || 0 } }
      }
      const list = c.replies?.list || []
      const updatedList = list.map((r) => String(r.id) === String(contentIdOrCommentId) ? { ...r, is_liked: res?.liked || false, interactions: { ...r.interactions, like_count: res?.like_count || 0 } } : r)
      return { ...c, replies: { ...c.replies, list: updatedList } }
    }))
    return res
  }

  /**
   * 创建评论/回复
   */
  const createComment = async (cid, payload) => {
    return await commentService.createComment(cid, payload)
  }

  return {
    comments,
    pagination,
    loading,
    error,
    loadComments,
    addCommentTop,
    addReplyTop,
    expandReplies,
    isExpanded,
    toggleLike,
    createComment,
    setComments,
    setPagination,
  }
}